// useRecordDetail hook — query-driven data loading for record detail page

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { usePromptProfiles } from '@/hooks/usePromptProfiles';
import { useCrmStageNames } from '@/hooks/useCrmStagesReference';
import { stageNamesOfInstallation } from '@/lib/crm-stage-names';
import { recordDetailOptions, recordTranscriptOptions } from '@/lib/query-options';
import { findRecordListItem } from '@/lib/records/list-cache';
import {
  getCachedAudioUrl,
  AudioAccessChangedError,
  loadAudioBlobUrl,
  setActiveAudioRecord,
} from '@/lib/records/audio-blob-cache';
import type { AnalysisAnnotationsResponse, RecordWithDetails } from '@/lib/api/types';
import { useToast } from '@/contexts/ToastContext';
import { logError, logWarning } from '@/lib/logger';
import { invalidateBacklogSummaries } from '@/features/processing/invalidate-backlog-summaries';
import { ApiError } from '@/lib/fetch-utils';
import {
  toAnalysisData,
  toUiRecordWithDetails,
  resolveDisplayStatus,
  type AnalysisData,
} from '@/lib/api/mappers';
import type { Annotation } from '@/components/details/TranscriptViewer';
import type { ScriptAdherenceData } from '@/components/details/ScriptAdherenceTab';
import type { AudioPlayerHandle } from '@/components/details/AudioPlayer';
import type { MediaCheck } from '@/components/data/media-check-history';
import { openPrintReport } from '@/lib/print-report';
import type { PrintReportInput } from '@/lib/print-report/types';
import type {
  BuiltinResultSources,
  RecordCriteriaResponse,
} from '@aica/api-types/schemas';
import {
  isChatTooLongRecord,
  isReanalyzeableRecordStatus,
  isRestartPendingRecord,
  isTranscriptReadyRecordStatus,
} from '@/lib/records/status';
import { useReanalyzeStatus } from '@/hooks/records/useReanalyzeStatus';
import {
  isRetryRunInFlight,
  processingRefetchInterval,
  retryRefetchInterval,
  startRetryWatch,
  type RetryWatch,
} from '@/hooks/records/retry-watch';

import {
  audioRefetchInterval,
  isAudioArchivePending,
  isDurableAudioUri,
  type AudioState,
} from '@/hooks/records/audio-readiness';
import { useTranslation } from 'react-i18next';
import { retryTransientOnly } from '@/lib/query-retry';
import { isProcessingPausedError } from '@/lib/api/processing-paused';
import { toastProcessingPaused } from '@/features/processing/processing-paused-notice';

/**
 * The cached queries a finished run re-derives — one list for the re-analysis
 * and the retry, so a query added later cannot be refreshed by one path and
 * missed by the other (#1096, code review).
 */
const RUN_DERIVED_QUERIES = ['transcript', 'script-adherence', 'criteria'] as const;

/**
 * Queries whose key also carries the analysis they were fetched beside, so the
 * entries of the record's other analyses are stale too: matched by prefix.
 */
const ANALYSIS_KEYED_QUERIES: ReadonlySet<string> = new Set(['transcript', 'criteria']);

function invalidateRunDerived(
  queryClient: ReturnType<typeof useQueryClient>,
  recordId: string | undefined,
  orgId: string | null | undefined
) {
  // The transcript and the criteria by prefix: their keys also carry the
  // analysis they were fetched beside (#1851, #2582), and every one of the
  // record's entries is stale.
  for (const part of RUN_DERIVED_QUERIES) {
    void queryClient.invalidateQueries({
      queryKey: ['records', part, recordId, orgId],
      exact: !ANALYSIS_KEYED_QUERIES.has(part),
    });
  }
}

/** Источник встроенных ещё не пришёл: ни одной старой строки встроенного (#2518). */
const PENDING_BUILTIN_SOURCES: BuiltinResultSources = {};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Names the caller has already resolved from raw ids (prompt profile, CRM
 * stage/pipeline) — mirrors what `RecordDetailInline` computes via
 * `resolveStageBadgeData` and passes to `AnalysisPanel`. The hook has no
 * access to the prompt-profiles/CRM-stages reference lists itself, so it
 * cannot resolve these on its own; the caller must supply them.
 */
export type ResolvedAnalysisNames = Pick<
  PrintReportInput,
  'promptProfileName' | 'crmStageName' | 'crmPipelineName' | 'crmMultiplePipelines'
>;

/** A refusal on authorization grounds, as opposed to data that isn't ready. */
function isPermissionDenied(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 403;
}

interface RecordData extends RecordWithDetails {
  progressPercent?: number;
  currentStage?: string;
  isShortCall?: boolean | null;
}

/**
 * The annotations route keeps a project-membership gate the analysis does not
 * (#2914): a department head reads a subordinate's analysis by hierarchy, and
 * the annotations answer 403 (supervisor) or 404 (agent, `denyNotRecordOwner`).
 * Either means "closed to this reader", not "the analysis is missing".
 */
function isAnnotationsRefusal(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 403 || status === 404;
}

/**
 * What the Notes tab and every way to annotate may rely on. Only `ready`
 * offers writing: `denied` says the notes are closed, `failed` that they did
 * not load — neither may pass for "no notes yet".
 */
export type AnnotationsState = 'loading' | 'ready' | 'denied' | 'failed';

/** The notes and whether the reader may add one (`canAnnotate`, #2915). */
interface AnnotationsRead {
  annotations: Annotation[];
  canAnnotate: boolean;
}

function extractAnnotations(data: AnalysisAnnotationsResponse): AnnotationsRead {
  return {
    annotations: Array.isArray(data.annotations)
      ? (data.annotations as Annotation[])
      : [],
    // Not said — not offered: a write the server may refuse is not offered.
    canAnnotate: data.canAnnotate === true,
  };
}

export function useRecordDetail(
  recordId: string,
  shouldFetchScriptAdherence: boolean = false,
  options?: {
    isEmbedded?: boolean;
    apiClient?: ReturnType<typeof useAuth>['apiClient'];
    selectedOrgId?: string | null;
    bitrix24Domain?: string | null;
    /**
     * The org the caller selected `recordId` under (RecordsPage tracks this
     * across org switches — see useResetSelectionOnOrgChange). When given
     * and it no longer matches the live `selectedOrgId`, the detail query
     * stays disabled: an org switch landed between selection and this
     * render/update. This is independent of whatever render gate the caller
     * itself applies — the query stays safe even if that gate is bypassed by
     * an unlucky effect order. Omitted (undefined) by callers that don't
     * track a selection org (e.g. a standalone record-detail page); `null`
     * (org not yet known) is likewise never treated as a mismatch.
     */
    expectedOrgId?: string | null;
  }
) {
  const auth = useAuth();
  const isEmbedded = options?.isEmbedded === true;
  const selectedOrgId = isEmbedded ? (options?.selectedOrgId ?? null) : auth.orgId;
  const bitrix24Domain = options?.bitrix24Domain ?? null;
  const apiClient = options?.apiClient ?? auth.apiClient;
  const { toast } = useToast();
  const { t } = useTranslation(['records', 'common']);
  const queryClient = useQueryClient();

  const [currentTime, setCurrentTime] = useState(0);
  const audioPlayerRef = useRef<AudioPlayerHandle>(null);
  const [showReanalyze, setShowReanalyze] = useState(false);
  // 'auto' = no override: the backend resolves the bound profile for the
  // record's category (Radix Select forbids '' as an item value).
  const [selectedPromptProfile, setSelectedPromptProfile] = useState<string>('auto');
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);
  const [annotationSelectedText, setAnnotationSelectedText] = useState<string>('');
  const [annotationStartPos, setAnnotationStartPos] = useState<number>(0);
  const [annotationEndPos, setAnnotationEndPos] = useState<number>(0);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [analysisOverride, setAnalysisOverride] = useState<AnalysisData | null>(null);

  // Always fetch once org is known (not just when the reanalyze panel opens):
  // the analysis panel's "Оценено по профилю {profile}" caption (Phase B,
  // B-7) resolves analysis.promptProfileId -> name from this same list, on
  // every record view. `/api/v1/prompt-profiles` is standalone-only — NOT
  // mounted under embeddedDataRouter (apps/edge-api/src/index.ts) — so this
  // must stay gated off in embedded mode or every embedded record open fires
  // a guaranteed-404 request logged as a Sentry error. The badge/caption
  // simply stay hidden in embedded (reanalyze itself still isn't offered
  // there either — that's a UI affordance, not a data gate).
  const { data: promptProfilesData, isLoading: isPromptProfilesLoading } =
    usePromptProfiles(
      { enabled: !!selectedOrgId && !isEmbedded },
      isEmbedded ? apiClient : undefined,
      isEmbedded ? selectedOrgId : undefined
    );

  const isValidRecordId = UUID_REGEX.test(recordId);

  useEffect(() => {
    if (recordId && !isValidRecordId) {
      logWarning('Invalid record ID format (not UUID), skipping fetch', {
        component: 'useRecordDetail',
        recordId,
      });
    }
  }, [recordId, isValidRecordId]);

  // Seed the detail panel from the cached list row (the click came from the
  // list, so the row is almost always there). The header renders instantly
  // and — because the row carries status and analysisId — the transcript and
  // analysis queries start in parallel with GET /records/:id instead of
  // waterfalling behind it. Memoized per record: the lookup result also
  // backs seedQualityScore, which must stay stable across the
  // placeholder→real-data transition to avoid a badge flicker.
  const seedItem = useMemo(
    () => (isValidRecordId ? findRecordListItem(queryClient, recordId) : undefined),
    [queryClient, recordId, isValidRecordId]
  );

  // Set by «Retry processing» — see `retry-watch.ts` (#1096).
  const retryWatchRef = useRef<RetryWatch | null>(null);
  useEffect(() => {
    retryWatchRef.current = null;
  }, [recordId]);

  const recordQuery = useQuery({
    ...recordDetailOptions(apiClient, recordId, selectedOrgId, {
      enabled:
        !!recordId &&
        !!selectedOrgId &&
        isValidRecordId &&
        // Gate 2 (see options.expectedOrgId doc above): never fires for a
        // recordId that was selected under a different org than the one
        // now live.
        (options?.expectedOrgId === undefined ||
          options?.expectedOrgId === null ||
          options.expectedOrgId === selectedOrgId),
    }),
    // The archive copy lands moments after the record itself does. Without
    // this the open panel never learns that `fileUri` was rewritten to
    // `r2://…` (staleTime 5 min, no refetch on focus), which is why the audio
    // only appeared after a manual page reload. Stops on its own as soon as
    // the copy is there — or once waiting stops being reasonable.
    refetchInterval: (query) => {
      const fetched = query.state.data?.record;
      const now = Date.now();
      const audio = audioRefetchInterval(fetched?.fileUri, fetched?.createdAt, now);
      if (audio) return audio;
      return (
        retryRefetchInterval(fetched?.status, retryWatchRef.current, now) ||
        processingRefetchInterval(
          fetched?.status,
          fetched?.updatedAt ?? fetched?.createdAt,
          now
        )
      );
    },
    // RecordListItem is structurally a valid (partial) RecordWithDetails —
    // detail-only fields like fileUri are optional and simply absent until
    // the real response replaces the placeholder.
    placeholderData: seedItem ? { record: seedItem } : undefined,
  });

  const record = useMemo(() => {
    if (!recordQuery.data?.record) {
      return null;
    }
    return toUiRecordWithDetails(recordQuery.data.record) as RecordData;
  }, [recordQuery.data]);

  // The retried run ended: the analysis it produced also re-derived script
  // adherence and criteria, which sit in their own cached queries — the same
  // set the re-analysis completion path invalidates (#1096, Codex) — and the
  // list row still shows the status it had when the run started. Runs when the
  // record CHANGES — its status or its `updatedAt` — so the old failure read
  // right after the POST (nothing changed) does not count as the end, while a
  // run that failed again into the status it started from (`analysis_failed`
  // → `pending` → `analysis_failed` between two polls, only `updatedAt`
  // moved) does (#1264, Codex). The server claims the record before it
  // answers, so every read after the POST that differs is the run's.
  const recordStatus = record?.status;
  const recordUpdatedAt = recordQuery.data?.record.updatedAt ?? null;
  useEffect(() => {
    if (retryWatchRef.current === null || isRetryRunInFlight(recordStatus)) return;
    retryWatchRef.current = null;
    invalidateRunDerived(queryClient, recordId, selectedOrgId);
    void queryClient.invalidateQueries({ queryKey: ['records', 'infinite'] });
  }, [recordStatus, recordUpdatedAt, queryClient, recordId, selectedOrgId]);

  // CRM deal pipelines/stages reference (Phase B, B-7) — resolves the
  // record's crmStageId/crmPipelineId snapshot to display names for the
  // stage badge. Soft-fails to `undefined` (no CRM connection, the CRM did
  // not answer) — the badge hides rather than showing raw IDs. The names are
  // those of EVERY CRM of the organization (`/crm/stage-names`, #2872) and the
  // record takes the list of its own install: `/crm/stages` describes the first
  // install only, so a Uspacy record would get Bitrix24's name. Asked in both
  // shells: the embedded router mounts it too
  // (`/api/v1/embedded/crm/stage-names`, same permission check). Gated on the
  // record actually carrying a captured stage snapshot (crmStageId != null) and
  // the install it came from — otherwise every record open fires live CRM API
  // calls even when there is nothing to resolve (flag off = always; Final B
  // review finding), and a record with no install takes no name anyway.
  const { data: crmStageNamesData } = useCrmStageNames({
    enabled:
      !!selectedOrgId && record?.crmStageId != null && record?.installationId != null,
  });
  const crmStagesReferenceData = stageNamesOfInstallation(
    crmStageNamesData,
    record?.installationId
  );

  const isMissedCall = record?.callCategory === 'missed';
  const isShortCategory = record?.callCategory === 'short';
  const skipTranscriptAndAnalysis = isMissedCall || isShortCategory;
  const analysisId = recordQuery.data?.record.analysisId ?? null;
  // The seed may predate the analysisId field in the list payload (stale
  // cache from an older deploy). Distinguish "no analysis exists" (null) from
  // "not known yet" (absent) so the analysis section shows a skeleton — not a
  // premature "no analysis" message — until the real record answers.
  const analysisKnown =
    !recordQuery.isPlaceholderData || 'analysisId' in (recordQuery.data?.record ?? {});
  // org id in queryKey so org switch invalidates cached transcript/analysis
  // for the same recordId in the other org's tenant.
  const analysisQueryKey = useMemo(
    () =>
      analysisId
        ? (['analyses', 'detail', analysisId, selectedOrgId] as const)
        : (['analyses', 'detail', 'pending', recordId, selectedOrgId] as const),
    [analysisId, recordId, selectedOrgId]
  );
  const annotationsQueryKey = useMemo(
    () => ['analyses', 'annotations', analysisId, selectedOrgId] as const,
    [analysisId, selectedOrgId]
  );

  // Транскрипт нужен не только успешному пути. Панель детали предлагает
  // «Переанализ» на сбойных статусах (их принимает бэкенд, см.
  // REANALYZEABLE_STATUSES), а у `analysis_failed`/`delivery_failed` транскрипт
  // уже есть — анализ падает ПОСЛЕ распознавания. Пока запрос был ограничен
  // TRANSCRIPT_READY_RECORD_STATUSES, `transcript` оставался null и
  // `handleReanalyze` молча выходил: ни запроса, ни тоста, ни спиннера.
  // `failed` может означать и падение до распознавания — тогда запрос ответит
  // 404, и это ровно тот honest empty, который уже логируется предупреждением.
  // Запись, которую повтор оставил ждать запуска (#1264), транскрипт тоже уже
  // имеет — панель обещает показать то, что дал прошлый прогон. Как и чат,
  // закрытый как слишком длинный (#1489): он сохранён, анализа нет.
  const shouldFetchTranscript =
    !!selectedOrgId &&
    !!recordId &&
    !skipTranscriptAndAnalysis &&
    !!recordQuery.data?.record &&
    (isTranscriptReadyRecordStatus(recordQuery.data.record.status) ||
      isReanalyzeableRecordStatus(recordQuery.data.record.status) ||
      isRestartPendingRecord(recordQuery.data.record) ||
      isChatTooLongRecord(recordQuery.data.record));

  const transcriptQuery = useQuery({
    ...recordTranscriptOptions(apiClient, recordId, selectedOrgId, analysisId),
    enabled: shouldFetchTranscript,
    // No placeholder when the key moves to a new analysis (#1851): the new
    // analysis's role map would label the previous transcript's `speaker_N`
    // as "Agent/Client" until the new one arrives — the very mislabel this
    // key exists to prevent. A brief skeleton is the honest state.
  });

  const transcript = transcriptQuery.data ?? null;

  const analysisQuery = useQuery({
    queryKey: analysisQueryKey,
    enabled: !!selectedOrgId && !!analysisId && !skipTranscriptAndAnalysis,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryTransientOnly,
    queryFn: async (): Promise<AnalysisData | null> => {
      if (!analysisId) return null;
      const apiAnalysis = await apiClient.analyses.getAnalysisById(analysisId);
      return apiAnalysis ? toAnalysisData(apiAnalysis) : null;
    },
  });

  // A query of their own, not a second half of the analysis query (#2914):
  // the analysis admits a department head by hierarchy and the annotations do
  // not, so their refusal must cost the annotations only. As an error of this
  // query it is also retried on the next open, unlike cached data.
  const annotationsQuery = useQuery({
    queryKey: annotationsQueryKey,
    enabled: !!selectedOrgId && !!analysisId && !skipTranscriptAndAnalysis,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryTransientOnly,
    queryFn: async (): Promise<AnnotationsRead> => {
      if (!analysisId) return { annotations: [], canAnnotate: false };
      return extractAnnotations(
        await apiClient.analyses.getAnalysisAnnotations(analysisId)
      );
    },
  });

  const analysis = analysisOverride ?? analysisQuery.data ?? null;
  const annotations = annotationsQuery.data?.annotations ?? [];
  const annotationsWritable = annotationsQuery.data?.canAnnotate ?? false;
  const annotationsState: AnnotationsState = annotationsQuery.data
    ? 'ready'
    : annotationsQuery.error
      ? isAnnotationsRefusal(annotationsQuery.error)
        ? 'denied'
        : 'failed'
      : annotationsQuery.isLoading
        ? 'loading'
        : 'ready';

  /**
   * The record's contents were refused, not merely absent.
   *
   * `GET /records/:id` carries no RBAC, so a supervisor with no project
   * assignment loads the header and then gets 403 on both the transcript and
   * the analysis. Every one of those failures used to collapse into `null`,
   * which the panel rendered as "processing may still be in progress" — a
   * report about the pipeline for what is a permissions problem, and the exact
   * complaint that surfaced this bug in production.
   */
  const accessDenied =
    isPermissionDenied(analysisQuery.error) || isPermissionDenied(transcriptQuery.error);

  const scriptAdherenceQuery = useQuery({
    queryKey: ['records', 'script-adherence', recordId, selectedOrgId] as const,
    enabled: !!selectedOrgId && !!recordId && shouldFetchScriptAdherence,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryTransientOnly,
    queryFn: async () => {
      const response = await apiClient.records.getRecordScriptAdherence(recordId);
      return response.adherence as ScriptAdherenceData | null;
    },
  });

  const scriptAdherence = scriptAdherenceQuery.data ?? null;
  const scriptAdherenceLoading = scriptAdherenceQuery.isLoading;

  /** Запись и её анализ известны — критерии можно просить для анализа панели (#2582). */
  const criteriaReady = !!recordQuery.data?.record && analysisKnown;
  /**
   * Анализ, который панель рисует СЕЙЧАС: завершённый переразбор показывает
   * новый анализ сразу (`analysisOverride`), раньше, чем детали записи
   * назовут его `record.analysisId`. Критерии идут за показанным анализом, а не
   * за полем записи, иначе до обновления деталей рядом с новым анализом
   * лежали бы критерии прошлого (и попали бы в PDF).
   */
  // Переопределение — только своей записи: при смене записи сброс идёт эффектом
  // на render позже, и чужой анализ не должен попасть в первый же запрос.
  const overrideAnalysisId =
    analysisOverride?.recordId === recordId ? analysisOverride.id : null;
  const panelAnalysisId = overrideAnalysisId ?? analysisId;

  /**
   * Результаты критериев на записи (#862) — группа «Критерии» в панели и
   * зеркалящий её раздел PDF.
   *
   * Запрос не привязан к признаку соответствия скрипту: критерии есть и там,
   * где скрипта нет. Пустой ответ — обычное дело (организация их не
   * настраивала), и панель просто не рисует группу.
   *
   * **Ответ хранится ЦЕЛИКОМ, а не одним полем `criteria`.** Пока запись в
   * работе, маршрут отвечает `processing: true` с пустым списком; сохранив
   * только список, запрос закешировал бы пустоту на пять минут и группа не
   * появилась бы после того, как конвейер дописал результаты, — даже при
   * повторном открытии записи (находка Codex на #1121). Поэтому признак
   * остаётся в кеше и по нему идёт опрос.
   */
  const criteriaQuery = useQuery({
    // Ключ несёт анализ, который рисует панель: ответ другого анализа (до
    // переразбора) не подставляется, даже когда новый запрос не удался (#2582).
    queryKey: ['records', 'criteria', recordId, selectedOrgId, panelAnalysisId] as const,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryTransientOnly,
    // Опрос только пока разбор идёт: у завершённой записи результаты уже
    // окончательны, и повторять запрос незачем.
    refetchInterval: (query: { state: { data?: RecordCriteriaResponse } }) =>
      query.state.data?.processing === true ? 15_000 : false,
    // Без `async`: тело — один вызов, и обёртка добавила бы лишний промис
    // (`require-await` это и ловит).
    queryFn: (): Promise<RecordCriteriaResponse> =>
      apiClient.records.getRecordCriteria(recordId, panelAnalysisId),
    // Анализ записи должен быть известен: запрос «вслепую» прочитал бы текущий
    // анализ PG, а при его появлении ключ сменился бы и ушёл второй запрос.
    enabled: !!selectedOrgId && !!recordId && criteriaReady,
  });

  const criteria = criteriaQuery.data?.criteria ?? [];
  /**
   * Ответа для ЭТОГО анализа ещё нет — доли секунды после открытия записи или
   * после смены анализа переразбором (запись, ещё не назвавшая анализ, тоже).
   */
  const criteriaPending =
    criteriaQuery.isLoading || (!criteriaReady && recordQuery.isFetching);
  /**
   * Конвейер ещё работает над записью: результаты будут, но КОГДА — неизвестно.
   * Позднее медиа и чат, ждущий закрытия в CRM, держат запись в этом состоянии
   * часами, поэтому «идёт разбор» — не повод отказывать в экспорте, только повод
   * предупредить (находка `/code-review` на #1121).
   */
  const criteriaProcessing = criteriaQuery.data?.processing === true;
  /**
   * Группа рисует «загрузку» и пока разбор идёт: результаты ещё будут, и
   * молчание тут значило бы «критериев нет» — другое утверждение.
   */
  const criteriaLoading = criteriaPending || criteriaProcessing;
  /**
   * Запрос не удался. Пустой список вместо этого признака означал бы, что
   * недоступный Postgres и организация без критериев выглядят одинаково, а PDF
   * молча вышел бы без раздела (находка Codex на #1121).
   */
  const criteriaFailed = criteriaQuery.isError;
  /**
   * Источник результата встроенных на записи (Э2, #2518): панель и PDF
   * показывают старую строку ключа только при `legacy`. Нет поля — Э2
   * выключен или ответа для этого анализа нет: старые строки как раньше.
   *
   * Список и источники берутся из ОДНОГО ответа, а ответ — из записи кэша
   * анализа панели (ключ запроса, #2582): после переразбора запрос нового
   * анализа начинает с пустого места, и его неудача оставляет и список, и
   * источники пустыми — встроенный виден одной старой строкой, а не ещё и
   * строкой группы из кэша прошлого анализа. Неудачный повторный опрос ТОГО ЖЕ
   * анализа оставляет оба из кэша вместе: источник `answer` держит строку
   * группы, а не пускает рядом старую.
   *
   * Пока идёт ПЕРВЫЙ запрос, источник неизвестен, и старые строки встроенных
   * придержаны (пустой набор — ни одного `legacy`): иначе у организации с Э2
   * каждое открытие записи показывало бы вердикты, которые через миг пропадут.
   * Без Э2 они просто появляются вместе с ответом. PDF в это время не
   * выпускается (`criteriaPending` ниже).
   */
  const builtinSources = criteriaPending
    ? PENDING_BUILTIN_SOURCES
    : criteriaQuery.data?.builtinSources;
  /** Ключи встроенных на паузе Aelo — вместе с источниками (#2518). */
  const builtinPausedByAelo = builtinSources
    ? criteriaQuery.data?.builtinPausedByAelo
    : undefined;

  useEffect(() => {
    setAnalysisOverride(null);
  }, [analysisId, recordId]);

  useEffect(() => {
    const err = recordQuery.error;
    if (!err) {
      return;
    }

    const is404 =
      err instanceof Error &&
      (err.message.includes('404') || err.message.includes('Not Found'));

    if (!is404) {
      logError(
        'Failed to fetch record details',
        { recordId, orgId: selectedOrgId ?? undefined },
        err instanceof Error ? err : undefined
      );
    }
  }, [recordQuery.error, recordQuery.errorUpdatedAt, recordId, selectedOrgId]);

  useEffect(() => {
    if (!transcriptQuery.error || !shouldFetchTranscript) {
      return;
    }

    logWarning('Failed to load transcript', {
      component: 'useRecordDetail',
      recordId,
      status: recordQuery.data?.record.status,
    });
  }, [
    transcriptQuery.error,
    transcriptQuery.errorUpdatedAt,
    shouldFetchTranscript,
    recordId,
    recordQuery.data?.record.status,
  ]);

  useEffect(() => {
    if (!analysisQuery.error || !analysisId) {
      return;
    }

    logError(
      'Analysis not available',
      { component: 'useRecordDetail', recordId, analysisId },
      analysisQuery.error instanceof Error ? analysisQuery.error : undefined
    );
  }, [analysisQuery.error, analysisQuery.errorUpdatedAt, analysisId, recordId]);

  // A refusal is the reader's access, not a fault: only a failure is logged.
  useEffect(() => {
    if (!annotationsQuery.error || isAnnotationsRefusal(annotationsQuery.error)) return;

    logError(
      'Annotations not available',
      { component: 'useRecordDetail', recordId, analysisId },
      annotationsQuery.error instanceof Error ? annotationsQuery.error : undefined
    );
  }, [annotationsQuery.error, annotationsQuery.errorUpdatedAt, analysisId, recordId]);

  useEffect(() => {
    if (!criteriaQuery.error) {
      return;
    }

    logWarning('Failed to fetch record criteria', {
      component: 'useRecordDetail',
      recordId,
      error:
        criteriaQuery.error instanceof Error
          ? criteriaQuery.error.message
          : 'Unknown error',
    });
  }, [criteriaQuery.error, criteriaQuery.errorUpdatedAt, recordId]);

  useEffect(() => {
    if (!scriptAdherenceQuery.error || !shouldFetchScriptAdherence) {
      return;
    }

    logWarning('Failed to fetch script adherence', {
      component: 'useRecordDetail',
      recordId,
      error:
        scriptAdherenceQuery.error instanceof Error
          ? scriptAdherenceQuery.error.message
          : 'Unknown error',
    });
  }, [
    scriptAdherenceQuery.error,
    scriptAdherenceQuery.errorUpdatedAt,
    shouldFetchScriptAdherence,
    recordId,
  ]);

  const mediaChecks = useMemo<MediaCheck[]>(() => {
    if (!record || record.type !== 'call' || record.status !== 'awaiting_media') {
      return [];
    }

    const checks = record.mediaMeta?.checks;
    if (!Array.isArray(checks)) {
      logError('Invalid media_meta.checks in record response', {
        component: 'useRecordDetail',
        recordId,
        checks,
      });
      return [];
    }

    return checks.map((check, index) => ({
      id: `check-${index}`,
      attempt: index + 1,
      scheduledAt: check.checkedAt,
      executedAt: check.checkedAt,
      result:
        check.status === 'found'
          ? 'found'
          : check.status === 'not_found'
            ? 'notFound'
            : check.status === 'timeout'
              ? 'timeout'
              : 'error',
      mediaSource: null,
      details: {
        error: check.error,
        mediaUri: check.mediaUri,
      },
    }));
  }, [record, recordId]);

  // Full-panel loading only while there is NOTHING to show: no real data and
  // no list-cache seed (deep links). With a seed, isLoading is false (the
  // placeholder counts as data) and the panel renders progressively with
  // per-section flags below.
  const loading = !!selectedOrgId && isValidRecordId && recordQuery.isLoading;

  const transcriptLoading = shouldFetchTranscript && transcriptQuery.isLoading;

  const analysisLoading =
    !skipTranscriptAndAnalysis &&
    !analysisOverride &&
    (analysisQuery.isLoading || !analysisKnown);

  const error = useMemo(() => {
    if (!recordId) {
      return null;
    }

    if (!isValidRecordId) {
      return t('records.invalidRecordId');
    }

    const err = recordQuery.error;
    if (!err) {
      return null;
    }

    const is404 =
      err instanceof Error &&
      (err.message.includes('404') || err.message.includes('Not Found'));

    if (is404) {
      return 'Record not found. It may have been deleted or you may not have access.';
    }

    return err instanceof Error ? err.message : 'Failed to fetch record details';
  }, [recordId, isValidRecordId, recordQuery.error, t]);

  /**
   * Экспорт СИНХРОННЫЙ намеренно.
   *
   * Критерии грузятся своим запросом, и анализ из кеша приходит раньше:
   * документ, выпущенный до их прихода, НАВСЕГДА уходит без раздела
   * «Критерии», а тост сообщает об успехе (находка Codex на #1121). Первым
   * решением было ждать запрос внутри экспорта — и это породило две новые
   * находки того же ревью: `ensureQueryData` отдаёт из кеша ответ «разбор
   * идёт» как окончательный, а `window.open` после сетевого ожидания теряет
   * пользовательский жест и блокируется браузером.
   *
   * Поэтому ожидания здесь нет вовсе: состояние запроса читается на момент
   * клика.
   *
   * **Отказ — только пока идёт ПЕРВЫЙ запрос** (доли секунды; повторный клик
   * сработает). Неудача запроса и незакончившийся разбор экспорт не блокируют:
   * одна испорченная строка `call_criteria` или запись, часами ждущая позднего
   * медиа, иначе лишали бы организацию печати отчётов вообще — побочная
   * возможность закрывала бы основную (находка `/code-review` на #1121). В этих
   * случаях документ выходит, а тост говорит, чего в нём нет.
   */
  const handleExportPdf = (names: ResolvedAnalysisNames) => {
    if (!record || !analysis) {
      toast.error(
        t('records.exportPdf.cannotExport'),
        t('records.exportPdf.noAnalysisData')
      );
      return;
    }

    if (criteriaPending) {
      toast.error(
        t('records.exportPdf.cannotExport'),
        t('records.exportPdf.criteriaPending')
      );
      return;
    }

    try {
      setExportingPdf(true);

      openPrintReport({
        record: {
          id: record.id,
          type: record.type,
          createdAt: record.createdAt ?? new Date().toISOString(),
          participantName: record.participantName,
          projectName: record.projectName,
          projectId: record.projectId,
          durationSeconds: record.durationSeconds ?? undefined,
          callCategory: record.callCategory,
          crmStageProgression: record.crmStageProgression,
          crmProgressionEvaluatedAt: record.crmProgressionEvaluatedAt,
        },
        analysis: {
          id: analysis.id,
          result: analysis.result,
          componentScores: analysis.scores,
          voiceMetrics: analysis.voiceMetrics,
          verification: analysis.verification,
          scoredWithEarlierWeights: analysis.scoredWithEarlierWeights,
        },
        scriptAdherence,
        criteria,
        builtinSources,
        builtinPausedByAelo,
        ...names,
      });
      // Документ вышел — но если критериев в нём нет по причине, которой
      // пользователь не видит, он должен узнать сейчас: исправить уже
      // отправленный отчёт нельзя.
      if (criteriaFailed) {
        logWarning('PDF exported without criteria: request failed', {
          component: 'useRecordDetail',
          recordId: record.id,
        });
        toast.info(
          t('records.exportPdf.exported'),
          t('records.exportPdf.criteriaUnavailable')
        );
      } else if (criteriaProcessing) {
        toast.info(
          t('records.exportPdf.exported'),
          t('records.exportPdf.criteriaProcessing')
        );
      } else {
        toast.success(
          t('records.exportPdf.exported'),
          t('records.exportPdf.exportedMessage')
        );
      }
    } catch (err) {
      logError(
        'Failed to export PDF',
        { recordId: record.id },
        err instanceof Error ? err : undefined
      );
      toast.error(
        t('records.exportPdf.exportFailed'),
        t('records.exportPdf.exportFailedMessage')
      );
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSeekAudio = (timeInSeconds: number) => {
    audioPlayerRef.current?.seekAndPlay(timeInSeconds);
    setCurrentTime(timeInSeconds);
  };

  const handlePlayAudioSegment = (startSeconds: number, durationSeconds: number = 20) => {
    void audioPlayerRef.current?.playSegment(startSeconds, durationSeconds);
    setCurrentTime(startSeconds);
  };

  const handleAnnotationCreate = (
    selectedText: string,
    startPosition: number,
    endPosition: number
  ) => {
    setAnnotationSelectedText(selectedText);
    setAnnotationStartPos(startPosition);
    setAnnotationEndPos(endPosition);
    setShowAnnotationModal(true);
  };

  const handleAnnotationSuccess = async () => {
    if (!analysisId) {
      return;
    }

    try {
      const annotationsData = await apiClient.analyses.getAnalysisAnnotations(analysisId);
      queryClient.setQueryData<AnnotationsRead>(
        annotationsQueryKey,
        extractAnnotations(annotationsData)
      );
    } catch {
      logWarning('Failed to refetch annotations', {
        component: 'useRecordDetail',
        analysisId,
      });
    }
  };

  // No auto-select: the default 'auto' means "Auto — category profile"
  // (backend resolves the bound profile for the record's category). Picking
  // a profile is an explicit override, not a required choice. Derived
  // directly from the query (no local state/effect needed) now that the
  // query itself is always enabled once org is known, not just while the
  // reanalyze panel is open — see the comment at the usePromptProfiles call.
  const promptProfiles = promptProfilesData ?? [];
  const profilesLoading = isPromptProfilesLoading;

  // Refs for latest values — used in async callbacks to avoid stale closures
  const recordIdRef = useRef(recordId);
  const analysisQueryKeyRef = useRef(analysisQueryKey);
  const analysisIdRef = useRef(analysisId);
  useLayoutEffect(() => {
    recordIdRef.current = recordId;
    analysisQueryKeyRef.current = analysisQueryKey;
    analysisIdRef.current = analysisId;
  }, [recordId, analysisQueryKey, analysisId]);

  // The record's source has processing paused or withdrawn (#2709): the server
  // refuses every re-analysis and retry of it, and the reader is told why
  // instead of «the record has already changed».
  const showProcessingPaused = () => toastProcessingPaused(toast, t);

  const {
    status: reanalyzeStatus,
    error: reanalyzeError,
    isPolling: reanalyzing,
    startReanalyze,
    cancel: cancelReanalyze,
    elapsedSeconds,
  } = useReanalyzeStatus(
    {
      onComplete: (newAnalysis) => {
        // Guard: if user navigated to a different record since polling started,
        // skip stale state update. Compare ref (latest) vs captured snapshot.
        const capturedRecordId = recordId;
        if (recordIdRef.current !== capturedRecordId) return;

        setAnalysisOverride(newAnalysis);
        setShowReanalyze(false);

        // Use refs to get latest query key (may have changed during polling)
        if (analysisIdRef.current) {
          queryClient.setQueryData<AnalysisData | null>(
            analysisQueryKeyRef.current,
            newAnalysis
          );
        }

        // exact: true requires the full queryKey including selectedOrgId,
        // otherwise the invalidation silently no-ops after we added org
        // scoping to the keys above.
        void queryClient.invalidateQueries({
          queryKey: ['records', 'detail', recordId, selectedOrgId],
          exact: true,
        });

        invalidateRunDerived(queryClient, recordId, selectedOrgId);

        toast.success(
          t('records.reanalyze.success'),
          t('records.reanalyze.successMessage')
        );
      },
      onError: (msg) => {
        toast.error(t('records.reanalyze.failed'), msg);
      },
      onProcessingPaused: showProcessingPaused,
      onTimeout: () => {
        toast.error(t('records.reanalyze.failed'), t('records.reanalyze.statusTimeout'));
      },
    },
    isEmbedded ? apiClient : undefined
  );

  // Stop polling on the previous record when the user navigates to another one.
  // The hook itself only cancels on unmount; without this the polling timer keeps
  // hitting /records/<old-id> for up to 2 minutes after navigation.
  useEffect(() => {
    return () => {
      cancelReanalyze();
    };
  }, [recordId, cancelReanalyze]);

  const handleReanalyze = async () => {
    if (!recordId) return;

    if (!selectedOrgId) {
      toast.error(t('records.selectOrganization'));
      return;
    }

    // Объект транскрипта здесь не нужен вообще — ни звонку, ни чату. Прежний
    // общий `!transcript` делал кнопку молчаливой заглушкой на каждом сбойном
    // статусе: ни запроса, ни ошибки, ни объяснения, и повторные нажатия тоже
    // ничего не давали.
    //
    // Режим один на оба типа — `from_asr_source`, корень линии ingest.
    // `resolveReplayTranscript` ищет его как `variant IN ('asr_source','manual')`
    // с `source_transcript_id IS NULL`, а у чата ingest пишет ровно такой
    // 'manual'-корень. Второй режим (`analysis_only`) тут не годится: он требует
    // УЖЕ существующий анализ этого транскрипта, а сбойный чат чаще всего до
    // анализа не дошёл — бэкенд ответил бы REPLAY_TRANSCRIPT_UNAVAILABLE, то
    // есть кнопка снова оказалась бы мёртвой, только с другим сообщением.
    await startReanalyze(
      recordId,
      selectedPromptProfile === 'auto' ? undefined : selectedPromptProfile || undefined,
      analysisId ?? null,
      { mode: 'from_asr_source' }
    );
  };

  const [isResumingHalted, setIsResumingHalted] = useState(false);

  /**
   * Вернуть в обработку запись, закрытую по причине, которая перестала
   * действовать (#460).
   *
   * Не `reanalyze` и не ручной запуск: у такой записи нет ни медиа, ни
   * расшифровки — она ждёт повторной попытки ЗАБРАТЬ файл. Источник отключили,
   * а потом переподключили: помешала интеграция, а не содержимое разговора.
   *
   * Решение о допустимости — за сервером: он один знает, восстановима ли
   * причина, и отвечает 400 на остальные. Здесь только вызов и обновление
   * кэша, чтобы карточка перестала показывать «анализировать нечего» поверх
   * уже идущего конвейера.
   */
  const handleResumeHalted = async () => {
    if (!recordId || isResumingHalted) return;

    if (!selectedOrgId) {
      toast.error(t('records.selectOrganization'));
      return;
    }

    setIsResumingHalted(true);
    try {
      // Ответ читается, а не считается успехом по факту 200. Маршрут намеренно
      // отвечает `success: true` и тогда, когда запуск не состоялся (нет
      // привязки WORKFLOWS_API, не-2xx от неё) — запись при этом остаётся
      // закрытой. Сказать «вернули в обработку» на таком ответе значило бы
      // разойтись с тем, что человек увидит после обновления (ревью Codex
      // на #567).
      const result = (await apiClient.records.retryRecordMedia(recordId)) as {
        workflowTriggered?: boolean;
      };

      if (result?.workflowTriggered === false) {
        toast.error(
          t('records.halted.resume.failed', 'Could not resume this record'),
          t(
            'records.halted.resume.notStarted',
            'The pipeline did not start. The record stays closed — try again later.'
          )
        );
        return;
      }

      // exact: true и полный ключ с организацией — по той же причине, что у
      // повторного анализа выше: без `selectedOrgId` инвалидация молча
      // промахивается.
      void queryClient.invalidateQueries({
        queryKey: ['records', 'detail', recordId, selectedOrgId],
        exact: true,
      });
      void queryClient.invalidateQueries({ queryKey: ['records', 'infinite'] });

      toast.success(
        t('records.halted.resume.success', 'Back in processing'),
        t(
          'records.halted.resume.successMessage',
          'We will fetch the recording again and run the pipeline.'
        )
      );
    } catch (error) {
      if (isProcessingPausedError(error)) {
        showProcessingPaused();
        return;
      }
      toast.error(
        t('records.halted.resume.failed', 'Could not resume this record'),
        error instanceof Error ? error.message : undefined
      );
    } finally {
      setIsResumingHalted(false);
    }
  };

  // The record whose retry is being sent — not a plain flag: the panel is
  // reused across records, and a POST that resolves after the user moved on
  // must neither spin the next record's button nor arm its watch.
  const [retryingFor, setRetryingFor] = useState<string | null>(null);
  const isRetryingFailed = retryingFor !== null && retryingFor === recordId;

  /**
   * Повторить обработку записи, упавшей на одном из шагов (#1096).
   *
   * `retry-transcription` — «умный» повтор, тот же, что у массовой
   * «Переобробки»: без транскрипта распознаёт заново, с транскриптом —
   * анализирует и доставляет заново. Не `reanalyze`: тот — реплей без
   * доставки в CRM и без транскрипта мёртв (REPLAY_TRANSCRIPT_UNAVAILABLE),
   * а у `transcription_failed` транскрипта нет по определению.
   *
   * Сбой запуска сервер отдаёт не-2xx, а не `success: false` (в отличие от
   * возврата в обработку выше), поэтому успех здесь — сам ответ.
   */
  const handleRetryFailed = async () => {
    if (!recordId || isRetryingFailed) return;

    if (!selectedOrgId) {
      toast.error(t('records.selectOrganization'));
      return;
    }

    const target = recordId;
    setRetryingFor(target);
    try {
      const result = await apiClient.records.retryTranscription(target);
      const waitsForStart = result.status === 'awaiting_start';

      // Before the invalidation: its refetch is what re-evaluates the interval.
      // Only for the record still open — the watch belongs to the panel. A
      // record left waiting for a start runs nothing, so there is nothing to
      // watch: the refetch below shows it with its start button.
      if (recordIdRef.current === target && !waitsForStart) {
        retryWatchRef.current = startRetryWatch(Date.now());
      }

      // Тот же полный ключ, что у возврата в обработку выше.
      void queryClient.invalidateQueries({
        queryKey: ['records', 'detail', target, selectedOrgId],
        exact: true,
      });
      void queryClient.invalidateQueries({ queryKey: ['records', 'infinite'] });

      if (waitsForStart) {
        invalidateBacklogSummaries(queryClient);
        toast.info(
          t(
            'records.failedNotice.retry.awaitingStart',
            'The record is waiting to be started'
          ),
          result.waitReason === 'project_analysis_off'
            ? t(
                'records.failedNotice.retry.awaitingStartProject',
                'Auto-Analysis is off in this project, so nothing was run. Start the record to analyse it.'
              )
            : t(
                'records.failedNotice.retry.awaitingStartManual',
                "Right now it's manual mode, so nothing was run. Start the record to analyse it."
              )
        );
      } else {
        toast.success(
          t('records.failedNotice.retry.success', 'Retry sent'),
          t(
            'records.failedNotice.retry.successMessage',
            'The record updates here as processing moves on.'
          )
        );
      }
    } catch (err) {
      // The record's source is stopped (#2709): an expected refusal with its
      // own explanation, not a failure to report — checked before the log so
      // it never reaches Sentry.
      if (isProcessingPausedError(err)) {
        showProcessingPaused();
        return;
      }
      // The request may never have reached the server (network, or an answer
      // that failed its schema) — the log is the only trace of that.
      logError(
        'Failed to retry a failed record',
        { recordId: target, orgId: selectedOrgId },
        err instanceof Error ? err : undefined
      );
      // 409/400: the record is no longer what the click saw — a run took it,
      // it already waits for a start, or it cannot be retried from what it
      // has. «Try again in a minute» would send the reader into a loop that
      // cannot succeed; show the record as it is now instead (#1264, code
      // review).
      const status = (err as { status?: unknown } | null)?.status;
      const code = (err as { data?: { code?: unknown } } | null)?.data?.code;
      // The record would wait for a manual start, and only a client or admin
      // can make one — a project supervisor can retry, not start (#1264). By
      // its code, not by 403 alone: an expired session or a right lost since
      // the panel loaded is a 403 too, and «an administrator needs to start
      // it» would name the wrong next step (Codex).
      if (code === 'RETRY_NEEDS_START') {
        toast.info(
          t(
            'records.failedNotice.retry.needsStart',
            'An administrator needs to start it'
          ),
          t(
            'records.failedNotice.retry.needsStartMessage',
            'Here a retry would leave the record waiting for a manual start, and only a client or admin can start it.'
          )
        );
        return;
      }
      // A chat whose text was never saved cannot wait for a manual start:
      // it stays failed, and the reader is told why (#1264).
      if (code === 'RETRY_NEEDS_TEXT') {
        toast.info(
          t('records.failedNotice.retry.needsText', 'This chat cannot wait for a start'),
          t(
            'records.failedNotice.retry.needsTextMessage',
            'Its text was never saved, so a manual start would have nothing to send. The chat stays as it is.'
          )
        );
        return;
      }
      if (status === 409 || status === 400) {
        void queryClient.invalidateQueries({
          queryKey: ['records', 'detail', target, selectedOrgId],
          exact: true,
        });
        toast.info(
          t('records.failedNotice.retry.stale', 'The record has already changed'),
          t(
            'records.failedNotice.retry.staleMessage',
            'It has been refreshed here. Check its current status before retrying.'
          )
        );
        return;
      }
      // The server's own message is English and technical; the reader gets
      // their language and the next step instead.
      toast.error(
        t('records.failedNotice.retry.failed', 'Could not restart processing'),
        t(
          'records.failedNotice.retry.failedMessage',
          'Try again in a minute. If it keeps failing, contact support.'
        )
      );
    } finally {
      setRetryingFor((current) => (current === target ? null : current));
    }
  };

  // The URL is held with the record it was loaded for. The effect below
  // replaces it a render AFTER `recordId` changed, so for that render the
  // previous record's URL (and its still-mounted player) stands under the new
  // record: `audioRecordId` says whose audio it is, and a link moment of the
  // new record waits for its own audio (#2966).
  const [loadedAudio, setLoadedAudio] = useState<{
    recordId: string;
    url: string;
  } | null>(null);
  const audioUrl = loadedAudio?.url ?? null;
  const audioRecordId = loadedAudio?.recordId ?? null;
  // Set when the blob request came back empty-handed (403/404/error). Kept
  // separate from `audioUrl === null`, which also covers "still downloading" —
  // without it the slot could not tell "on its way" from "not coming".
  const [audioFailed, setAudioFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    // Pin this record's blob URL against LRU eviction: with rapid record
    // switches, concurrent downloads resolve in arbitrary order, and a stale
    // record's late completion could otherwise evict (and revoke) the URL
    // the CURRENT player is using — MediaElement then dies with
    // "Format error". Cleanup unpins; the next record's effect re-pins.
    setActiveAudioRecord(recordId || null);

    async function loadAudio() {
      setAudioFailed(false);

      if (!record?.fileUri || !recordId) {
        setLoadedAudio(null);
        return;
      }

      // A non-`r2://` value is the raw provider URL ingest wrote before the
      // archive step ran: cross-origin, provider-authenticated, presigned for
      // minutes. Feeding it to the player is what made a freshly imported
      // record fail until a manual reload. Wait for our own copy instead —
      // the detail query polls for it (see `audioRefetchInterval`).
      if (!isDurableAudioUri(record.fileUri)) {
        setLoadedAudio(null);
        return;
      }

      // Cache hit (recently viewed record) → instant, no re-download.
      const cached = getCachedAudioUrl(recordId);
      if (cached) {
        setLoadedAudio({ recordId, url: cached });
        return;
      }

      // Cache miss: clear the previous record's URL FIRST so its player
      // unmounts instead of keeping the old audio playable while the new
      // file downloads.
      setLoadedAudio(null);

      try {
        // The cache owns the object-URL lifecycle (revokes on LRU eviction),
        // so there is nothing to revoke on unmount. A download that resolves
        // after the user switched away still lands in the cache — switching
        // back is then instant.
        const url = await loadAudioBlobUrl(recordId, () =>
          apiClient.records.getRecordAudioBlob(recordId)
        );
        if (isMounted) {
          setLoadedAudio({ recordId, url });
        }
      } catch (err) {
        // The access changed under the download: the panels close with it, and
        // the recording was dropped on purpose — not a failure to report.
        if (err instanceof AudioAccessChangedError) return;
        // 403/404 are expected for roles without record.read on the project
        // (agent opening own call) or records without audio — warning, not
        // an error event. Anything else is a real failure.
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === 403 || status === 404) {
          logWarning('Failed to load audio file', { recordId, status });
        } else {
          logError(
            'Failed to load audio file',
            { recordId },
            err instanceof Error ? err : undefined
          );
        }
        if (isMounted) {
          setLoadedAudio(null);
          setAudioFailed(true);
        }
      }
    }

    void loadAudio();

    return () => {
      isMounted = false;
      setActiveAudioRecord(null);
    };
  }, [record?.fileUri, recordId, apiClient]);

  // What the audio slot should show. `audioUrl === null` alone cannot say it:
  // it covers the file being on its way and the file never coming, and the
  // panel has to render those differently — a skeleton that never resolves
  // claims a file is loading when nothing is.
  const audioState: AudioState = audioUrl
    ? 'ready'
    : // Seed row from the list cache: fileUri is a detail-only field and is
      // simply not known yet, so reserve the slot rather than deny it.
      recordQuery.isPlaceholderData ||
        isAudioArchivePending(record?.fileUri, record?.createdAt, Date.now()) ||
        (isDurableAudioUri(record?.fileUri) && !audioFailed)
      ? 'preparing'
      : 'unavailable';

  return {
    apiClient,
    record,
    transcript,
    analysis,
    scriptAdherence,
    scriptAdherenceLoading,
    criteria,
    criteriaLoading,
    criteriaFailed,
    builtinSources,
    builtinPausedByAelo,
    criteriaProcessing,
    mediaChecks,
    annotations,
    loading,
    transcriptLoading,
    analysisLoading,
    // The contents were refused, not missing — the panel must say so instead of
    // blaming the pipeline.
    accessDenied,
    // Whether the annotations are there to show and to write (#2914): the
    // analysis can be readable while they are refused.
    annotationsState,
    annotationsWritable,
    // True while the panel runs on the list-cache seed (detail-only fields
    // like fileUri are not available yet).
    isSeedRecord: recordQuery.isPlaceholderData,
    // Header score fallback while the analysis is still loading. Taken from
    // the list row (same source: latest analysis metrics) and stable across
    // the placeholder→real-data transition.
    seedQualityScore: seedItem?.qualityScore ?? null,
    error,
    audioUrl,
    audioRecordId,
    audioState,
    audioPlayerRef,
    currentTime,
    setCurrentTime,
    bitrix24Domain,
    resolveDisplayStatus,
    showReanalyze,
    setShowReanalyze,
    selectedPromptProfile,
    setSelectedPromptProfile,
    promptProfiles,
    profilesLoading,
    // CRM deal pipelines/stages reference — for resolving the stage badge's
    // stage/pipeline names client-side (Phase B, B-7). `[]` covers both "not
    // loaded yet" and "unavailable" — either way the badge has nothing to
    // show and hides.
    crmStagesReference: crmStagesReferenceData ?? [],
    reanalyzeStatus,
    reanalyzeError,
    reanalyzing,
    elapsedSeconds,
    cancelReanalyze,
    showAnnotationModal,
    setShowAnnotationModal,
    annotationSelectedText,
    annotationStartPos,
    annotationEndPos,
    exportingPdf,
    handleSeekAudio,
    handlePlayAudioSegment,
    handleAnnotationCreate,
    handleAnnotationSuccess,
    handleExportPdf,
    handleReanalyze,
    handleResumeHalted,
    isResumingHalted,
    handleRetryFailed,
    isRetryingFailed,
  };
}

export type UseRecordDetailReturn = ReturnType<typeof useRecordDetail>;
